import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	userManagementColumns,
} from "../../../utils/constants";
import AdminTables from "../../common/adminTables";
import { Alert as AlertType } from "../../../utils/interfaces";
import { ConfirmationModal } from "../../common/modal";
import adminConfirmation from "../../common/modal/modalBody/adminConfirmation";
import AdminLayout from "../../common/adminLayout";

const UserManagement = () => {
	const navigate = useNavigate();
	const { userData, logout } = useAuth();
	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(3);
	const [isLoading, setIsLoading] = useState(true);
	const [alert, setAlert] = useState<AlertType | null>(null);
	const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
	const [selectedRowData, setSelectedRowData] = useState<any>(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedAction, setSelectedAction] = useState<string | null>(null);
	const [selectedAccountType, setSelectedAccountType] = useState<number | null>(
		null
	);

	const handleAlert = (message: string, type: "success" | "danger") => {
		setAlert({
			message,
			type,
			show: true
		});
	};

	// Custom debounce hook
	function useDebounce<T extends (...args: any[]) => any>(
		callback: T,
		delay: number
	  ) {
		const timeoutRef = useRef<NodeJS.Timeout | null>(null);
		
		return useCallback((...args: Parameters<T>) => {
		  if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		  }
		  
		  timeoutRef.current = setTimeout(() => {
			callback(...args);
		  }, delay);
		}, [callback, delay]);
	}

	const debouncedFetchData = useDebounce((columnId: string, value: string) => {
		if (value.trim() === '') {
			// If search value is empty, reset to normal data
			getFormData();
			return;
		}
	
		// Proceed with search
		fetchFilteredUsers(columnId, value);
	}, 500);
	
	// Helper function to fetch filtered users
	const fetchFilteredUsers = async (columnId: string, value: string) => {
		try {
			// Create params object with pagination
			const params: Record<string, any> = {
				offset: (currentPage - 1) * itemsPerPage,
				limit: itemsPerPage,
			};
			
			// Add search parameters if column and value exist
			if (columnId && value) {
				buildSearchParams(params, columnId, value);
			}
			
			const response = await createAxiosInstance().get("/users", { params });
			setFormData(response.data);
		} catch (error: any) {
			handleSearchError(error);
		}
	};
	
	// Helper function to build search parameters
	const buildSearchParams = (params: Record<string, any>, columnId: string, value: string) => {
		if (columnId === 'full_name') {
			handleFullNameSearch(params, value);
		} else if (columnId === 'account_type') {
			handleAccountTypeSearch(params, columnId, value);
		} else {
			params[columnId] = value;
		}
	};
	
	// Helper function for full_name search
	const handleFullNameSearch = (params: Record<string, any>, value: string) => {
		const nameParts = value.trim().split(' ');
		if (nameParts.length > 1) {
			params['first_name'] = nameParts[0];
			params['last_name'] = nameParts.slice(1).join(' ');
		} else {
			params['first_name'] = value;
			// params['last_name'] = value;
		}
	};
	
	// Helper function for account_type search
	const handleAccountTypeSearch = (params: Record<string, any>, columnId: string, value: string) => {
		const accountTypeMap: Record<string, number> = {
			'free': 0,
			'premium': 1,
			'advisor': 2,
			'service provider': 3
		};
		
		const normalizedValue = value.trim().toLowerCase();
		if (normalizedValue in accountTypeMap) {
			params[columnId] = accountTypeMap[normalizedValue];
		} else {
			params[columnId] = value;
		}
	};
	
	// Helper function to handle search errors
	const handleSearchError = (error: any) => {
		handleApiError(error, "Error searching users");
		setFormData({ results: [], total_count: 0 });
		handleAlert("Error searching users", "danger");
	};

	const fetchDataFromServer = async (columnId: string, value: string) => {
		console.log(columnId, value, "columnId, value");
		debouncedFetchData(columnId, value);

	};

	const handleApiError = (error: any, message: string = "An error occurred") => {
		if (error.status == 401 || error.status == 403) {
			logout();
			navigate("/");
		}
		console.error(message, error);
	};
	
	const getUsers = async () => {
		try {
			const response = await createAxiosInstance().get("/users", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});

			return response;
		} catch (error: any) {
			handleApiError(error, "Error fetching users");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			const response = await getUsers();
			setFormData(response.data);
		} catch (error: any) {
			handleApiError(error, "Error loading form data");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getFormData();
	}, [currentPage, itemsPerPage]);

	useEffect(() => {
		if (userData && !userData?.is_admin) {
			navigate("/");
		}
	}, [userData]);

	const handleRowSelection = (rowData: any) => {
		if (rowData?.id !== selectedRowId) {
			setSelectedRowId(rowData?.id);
			setSelectedRowData(rowData);
			console.log(rowData, "Row data")
		} else {
			setSelectedRowId(null);
			setSelectedRowData(null);
		}
	};

	const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const action = e.target.value;
		if (action) {
			setSelectedAction(action);
			setShowModal(true);
		}
	};

	// Unified action configuration
	const actionConfig: Record<string, { endpoint: string, resultMessage: string }> = {
		"changeAccountType": { endpoint: "/update-account-access", resultMessage: "updated" },
		"delete": { endpoint: "/delete-user", resultMessage: "deleted" },
		"suspend": { endpoint: "/suspend-user", resultMessage: "suspended" },
		"reactivate": { endpoint: "/enable-user", resultMessage: "reactivated" }
	};

	// Helper function to determine the API endpoint
	const getActionEndpoint = (action: string): string => {
		return actionConfig[action]?.endpoint || `/users/${action}`;
	};
	
	// Helper function to get success message
	const getSuccessMessage = (action: string): string => {
		const resultMessage = actionConfig[action]?.resultMessage || action;
		return `Account with id ${selectedRowId} successfully ${resultMessage}`;
	};

	// Helper function to build the payload
	const buildActionPayload = (action: string): Record<string, any> => {
		if (action === "changeAccountType") {
			return {
				new_account_type: selectedAccountType,
				user_id: selectedRowId,
			};
		}
		
		return { user_id: selectedRowId };
	};

	const handleConfirm = async () => {
		if (!selectedAction) return;
		
		try {
			const url = getActionEndpoint(selectedAction);
			const payload = buildActionPayload(selectedAction);
			
			await createAxiosInstance().put(url, payload);
			handleAlert(getSuccessMessage(selectedAction), "success");
			getFormData();
			setSelectedRowId(null);
		} catch (error: any) {
			handleApiError(error, "Error performing action");
		} finally {
			setShowModal(false);
			setSelectedAction(null);
		}
	};

	const handleCancel = () => {
		setShowModal(false);
		setSelectedAction(null);
	};

	// Action panel component
	const ActionPanel = () => (
		<>
			{selectedRowId && (
				<>
					<label htmlFor="floatingSelect">User Actions:</label>
					<select
						onChange={handleSelectChange}
						className="form-select mb-4"
						id="floatingSelect"
						aria-label="Floating label select example"
					>
						<option value="">Select an action</option>
						<option value="delete">Delete</option>
						{selectedRowData?.is_enabled && <option value="suspend">Suspend</option>}
						{!selectedRowData?.is_enabled && <option value="reactivate">Reactivate</option>}
						
						<option value="changeAccountType">
							Change Account Type
						</option>
					</select>
				</>
			)}
		</>
	);

	return (
		<AdminLayout
			title="User Management"
			actionPanel={<ActionPanel />}
			formData={formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
			alert={alert || undefined}
			setAlert={setAlert}
		>
			<div className="d-flex justify-content-start">
				<i
					className="bi bi-info-circle text-secondary cursor-pointer"
					style={{ cursor: "pointer" }}
				></i>
				<p className="text-secondary px-2">
					Click on a user row to perform relevant actions.
				</p>
			</div>
			<AdminTables
				data={formData.results}
				columns={userManagementColumns}
				rowSelectionEnabled={true}
				onRowSelect={handleRowSelection}
				serverSideSearch={true}
				onServerSearch={(columnId, value) => {
					fetchDataFromServer(columnId, value);
				}}
				isLoading={isLoading}
			/>
			<ConfirmationModal
				show={showModal}
				onClose={handleCancel}
				onConfirm={handleConfirm}
				title={'Confirm Action'}
				body={adminConfirmation({ 
					action: selectedAction ?? "",
					selectedAccountType,
					setSelectedAccountType })}
				maxHeight={selectedAction === "changeAccountType" ? "30vh" : "20vh"}
				maxWidth={'40vw'}
			/>
		</AdminLayout>
	);
};

export default UserManagement;
